import api from './api';

class CreatorOnboardingService {
  // Banner state management
  async getBannerState() {
    try {
      const response = await api.get('/creator/banner-state');
      return response.data;
    } catch (error) {
      console.error('Error fetching banner state:', error);
      throw error;
    }
  }

  async trackBannerInteraction(action) {
    try {
      const response = await api.post('/creator/banner-interaction', {
        action: action, // 'clicked', 'dismissed', 'opted_out'
        timestamp: new Date().toISOString()
      });
      return response.data;
    } catch (error) {
      console.error('Error tracking banner interaction:', error);
      throw error;
    }
  }

  /**
   * Get complete onboarding status in a single API call
   * Returns: applicationStatus, verification, documents all in one call
   * @returns {Promise<Object>} - Unified status including application, verification, and documents
   */
  async getOnboardingStatus() {
    try {
      const response = await api.get('/creator/onboarding-status');
      return response.data;
    } catch (error) {
      console.error('Error fetching onboarding status:', error);
      throw error;
    }
  }

  // Application management
  async getApplicationStatus() {
    try {
      const response = await api.get('/creator/application-status');
      return response.data;
    } catch (error) {
      console.error('Error fetching application status:', error);
      throw error;
    }
  }

  async saveApplicationStep(stepData) {
    try {
      const response = await api.post('/creator/application-step', stepData);
      return response.data;
    } catch (error) {
      console.error('Error saving application step:', error);
      throw error;
    }
  }

  async getApplicationData() {
    try {
      const response = await api.get('/creator/application-data');
      return response.data;
    } catch (error) {
      console.error('Error fetching application data:', error);
      throw error;
    }
  }

  async submitApplication(applicationData) {
    try {
      const response = await api.post('/creator/submit-application', applicationData);
      return response.data;
    } catch (error) {
      console.error('Error submitting application:', error);
      throw error;
    }
  }

  async reapplyAfterRejection() {
    try {
      const response = await api.post('/creator/reapply');
      return response.data;
    } catch (error) {
      console.error('Error resetting rejected application:', error);
      throw error;
    }
  }

  // Validation helpers
  validatePersonalInfo(data) {
    const errors = {};

    if (!data.fullName || data.fullName.trim().length < 2) {
      errors.fullName = 'Full name is required and must be at least 2 characters';
    }

    if (!data.dateOfBirth) {
      errors.dateOfBirth = 'Date of birth is required';
    } else {
      const birthDate = new Date(data.dateOfBirth);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      if (age < 18) {
        errors.dateOfBirth = 'You must be at least 18 years old to become a creator';
      }
    }

    if (!data.country) {
      errors.country = 'Country is required';
    }

    if (!data.phoneNumber) {
      errors.phoneNumber = 'Phone number is required';
    } else if (!/^\+?[\d\s-()]{10,}$/.test(data.phoneNumber)) {
      errors.phoneNumber = 'Please enter a valid phone number';
    }

    if (!data.reasonForCreator || data.reasonForCreator.trim().length < 10) {
      errors.reasonForCreator = 'Please provide a reason (minimum 10 characters)';
    }

    if (!data.contentTypes || data.contentTypes.length === 0) {
      errors.contentTypes = 'Please select at least one content type';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }

  // Utility methods
  getDocumentTypeDisplayName(documentType) {
    const displayNames = {
      'passport': 'Passport',
      'drivers_license': 'Driver\'s License',
      'government_id': 'Government ID',
      'selfie_with_id': 'Selfie with ID'
    };
    return displayNames[documentType] || documentType;
  }

  getContentTypeDisplayName(contentType) {
    const displayNames = {
      'photography': 'Photography',
      'video': 'Video Content',
      'written': 'Written Content',
      'art_design': 'Art & Design',
      'music_audio': 'Music & Audio',
      'educational': 'Educational Content',
      'fitness_wellness': 'Fitness & Wellness',
      'technology': 'Technology',
      'other': 'Other'
    };
    return displayNames[contentType] || contentType;
  }

  getApplicationStatusDisplayName(status) {
    const displayNames = {
      'draft': 'Draft',
      'submitted': 'Under Review',
      'approved': 'Approved',
      'rejected': 'Rejected'
    };
    return displayNames[status] || status;
  }
}

export const creatorOnboardingService = new CreatorOnboardingService();
export default creatorOnboardingService;
